import React from 'react';
import FileUpload from '../FileUpload';
import { PPTProcessingStatus } from './PPTProcessingStatus';
import { FilesState, PPTTranscriptionState, FormErrors } from './types';

interface FileUploadSectionProps {
  files: FilesState;
  pptTranscription: PPTTranscriptionState;
  errors: FormErrors;
  handleFileSelected: (type: string) => (file: File) => void;
  handleFileUpload: (
    type: string
  ) => (url: string, fileName: string) => Promise<void>;
  handleFileUploadStart: (type: string) => void;
  handleFileUploadError: (type: string) => (error: unknown) => void;
  isFinalStage?: boolean;
}

export function FileUploadSection({
  files,
  pptTranscription,
  errors,
  handleFileSelected,
  handleFileUpload,
  handleFileUploadStart,
  handleFileUploadError,
  isFinalStage = false,
}: FileUploadSectionProps) {
  return (
    <div className="space-y-6 border-t border-gray-200 pt-6">
      <div>
        <h3 className="text-lg font-baijamjuree font-bold text-gray-900 mb-4">
          Supporting Materials
        </h3>
        <p className="text-sm text-gray-600 font-baijamjuree mb-6">
          Upload your presentation {isFinalStage ? 'and video ' : ''}materials
          to support your submission.
        </p>
      </div>

      <div
        className={`grid grid-cols-1 ${isFinalStage ? 'lg:grid-cols-2' : ''} gap-6`}
      >
        <FileUpload
          label="Presentation File"
          acceptedTypes={['pptx', 'pdf', 'ppt']}
          onFileSelected={handleFileSelected('presentation')}
          onFileUploaded={handleFileUpload('presentation')}
          onUploadStart={() => handleFileUploadStart('presentation')}
          onUploadError={handleFileUploadError('presentation')}
          isUploading={files.presentation.uploading}
          uploadedFileName={files.presentation.fileName}
          uploadedFileUrl={files.presentation.url}
          maxSizeGB={0.1}
        />

        {isFinalStage && (
          <FileUpload
            label="Video File"
            acceptedTypes={['mp4', 'mov', 'wmv']}
            onFileUploaded={handleFileUpload('video')}
            onUploadStart={() => handleFileUploadStart('video')}
            onUploadError={handleFileUploadError('video')}
            isUploading={files.video.uploading}
            uploadedFileName={files.video.fileName}
            uploadedFileUrl={files.video.url}
            maxSizeGB={0.1}
          />
        )}
      </div>

      {/* PPT Processing Status - shown as soon as transcription begins (parallel with upload) */}
      {(pptTranscription.isTranscribing ||
        pptTranscription.isCompleted ||
        !!pptTranscription.error) && (
        <PPTProcessingStatus pptTranscription={pptTranscription} />
      )}

      {/* File Upload Errors */}
      {errors.files && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
          <p className="text-sm text-red-600 font-baijamjuree">
            {errors.files.join(', ')}
          </p>
        </div>
      )}
    </div>
  );
}
